/**
 * Screenshot Listener Hook
 *
 * Listens for screenshots from the Chrome extension and handles them
 */

import { useEffect, useState } from 'react';
import { createLogger, randomUUID } from '@ctrl-enter/tools/utils';

const logger = createLogger('web:screenshot-listener');

export type Screenshot = {
  id: string;
  dataUrl: string;
  timestamp: number;
  tabInfo?: {
    title: string;
    url: string;
  };
};

export function useScreenshotListener() {
  const [screenshot, setScreenshot] = useState<Screenshot | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle screenshot received from extension
      if (event.data?.type === 'SCREEN_CAPTURE') {
        logger.info('📸 Screenshot received from extension:', {
          hasData: !!event.data.data,
          timestamp: event.data.timestamp,
        });

        setScreenshot({
          id: randomUUID(),
          dataUrl: event.data.data, // Note: extension sends as 'data', not 'screenshot'
          timestamp: event.data.timestamp || Date.now(),
          tabInfo: event.data.tabInfo,
        });
      }

      // Handle screenshot capture error from extension
      if (event.data?.type === 'SCREEN_CAPTURE_ERROR') {
        logger.error('❌ Screenshot capture failed:', event.data.error);

        // Could dispatch an event here for components to listen to
        window.dispatchEvent(
          new CustomEvent('screenshot-capture-error', {
            detail: { error: event.data.error },
          })
        );
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const clearScreenshot = () => {
    setScreenshot(null);
  };

  return {
    screenshot,
    clearScreenshot,
    hasScreenshot: screenshot !== null,
  };
}
